import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, FileDown, Printer, Save, Send, CopyPlus, Truck, CheckCheck, Ban, Undo2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { downloadFile } from "@/lib/utils2";
import { emptyImp, fromData, toPayload } from "@/lib/imp";
import { ImpForm } from "@/components/imp/ImpForm";
import { ImpView } from "@/components/imp/ImpView";
import { ImpSign } from "@/components/imp/ImpSign";
import { ImpBadge } from "@/pages/Impegnative";

const NEXT = {
  bozza: [["in_attesa_firma", "Pronta per la firma", Send]],
  in_attesa_firma: [["bozza", "Torna in bozza", Undo2]],
  firmata: [["in_consegna", "Metti in consegna", Truck]],
  in_consegna: [["consegnata", "Consegnata e completata", CheckCheck], ["firmata", "Torna a firmata", Undo2]],
};

function Actions({ imp, ver, onChange, onSave, dirty }) {
  const [busy, setBusy] = useState("");
  const cur = imp.versions[imp.versions.length - 1];
  const run = async (key, fn) => {
    setBusy(key);
    try { await fn(); } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(""); }
  };
  const status = (s) => run(s, async () => {
    if (dirty && !(await onSave())) return;
    const { data } = await api.post(`/impegnative/${imp.id}/status`, { status: s });
    onChange(data);
  });
  const pdfBlob = async () => (await api.get(`/impegnative/${imp.id}/pdf`, { params: { version: ver }, responseType: "blob" })).data;
  const print = () => run("print", async () => {
    const w = window.open("", "_blank");
    const url = URL.createObjectURL(await pdfBlob());
    if (w) w.location.href = url; else window.location.href = url;
  });
  return (
    <div className="flex flex-wrap gap-2" data-testid="imp-actions">
      <button onClick={() => run("pdf", () => downloadFile(`/impegnative/${imp.id}/pdf`, { version: ver }, `${imp.number}_v${ver}.pdf`))} className="btn-secondary h-10 px-4" data-testid="imp-pdf-button">
        {busy === "pdf" ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />} PDF
      </button>
      <button onClick={print} className="btn-secondary h-10 px-4" data-testid="imp-print-button"><Printer className="h-4 w-4" /> Stampa</button>
      {(NEXT[imp.status] || []).map(([s, l, Icon]) => (
        <button key={s} onClick={() => status(s)} disabled={!!busy} className="btn-primary h-10 px-4" data-testid={`imp-status-${s}-button`}><Icon className="h-4 w-4" /> {l}</button>
      ))}
      {cur.signature && !["consegnata", "annullata"].includes(imp.status) && (
        <button onClick={() => window.confirm("Creare una nuova versione modificabile? La versione firmata resterà conservata.") && run("nv", async () => onChange((await api.post(`/impegnative/${imp.id}/new-version`)).data))}
          className="btn-secondary h-10 px-4" data-testid="imp-new-version-button"><CopyPlus className="h-4 w-4" /> Nuova versione</button>
      )}
      {!["consegnata", "annullata"].includes(imp.status) && (
        <button onClick={() => window.confirm("Annullare questa impegnativa?") && status("annullata")} className="btn-ghost h-10 px-3 text-red-600" data-testid="imp-cancel-button"><Ban className="h-4 w-4" /> Annulla</button>
      )}
    </div>
  );
}

export default function ImpegnativaDetail() {
  const { id } = useParams();
  const isNew = id === "nuova";
  const navigate = useNavigate();
  const [imp, setImp] = useState(null);
  const [f, setF] = useState(isNew ? emptyImp() : null);
  const [ver, setVer] = useState(null);
  const [conditions, setConditions] = useState("");
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const apply = useCallback((d) => {
    setImp(d);
    setVer(d.current_version);
    setF(fromData(d.versions[d.versions.length - 1].data));
    setDirty(false);
  }, []);

  useEffect(() => {
    api.get("/settings").then(({ data }) => setConditions(data.impegnative_conditions || "")).catch(() => {});
    if (!isNew) api.get(`/impegnative/${id}`).then(({ data }) => apply(data)).catch((e) => { toast.error(formatApiError(e)); navigate("/impegnative"); });
  }, [id, isNew, apply, navigate]);

  const cur = imp && imp.versions[imp.versions.length - 1];
  const editable = isNew || (cur && !cur.signature && ["bozza", "in_attesa_firma"].includes(imp.status));

  useEffect(() => {
    if (isNew || editable) {
      return undefined;
    }
    const t = setInterval(() => api.get(`/impegnative/${id}`).then(({ data }) => setImp(data)).catch(() => {}), 15000);
    return () => clearInterval(t);
  }, [id, isNew, editable]);

  const setForm = (fn) => { setF(fn); setDirty(true); };

  const save = async () => {
    setSaving(true);
    try {
      if (isNew) {
        const { data } = await api.post("/impegnative", toPayload(f));
        toast.success(`Impegnativa ${data.number} creata`);
        navigate(`/impegnative/${data.id}`, { replace: true });
        return true;
      }
      const { data } = await api.put(`/impegnative/${id}`, toPayload(f));
      apply(data);
      toast.success("Impegnativa salvata");
      return true;
    } catch (e) {
      toast.error(formatApiError(e));
      return false;
    } finally {
      setSaving(false);
    }
  };

  if (!f || (!isNew && !imp)) return <p className="text-[var(--muted)]">Caricamento...</p>;
  const shown = imp && imp.versions.find((x) => x.version === ver);

  return (
    <div className="space-y-6 pb-20">
      <div className="space-y-3 fade-up">
        <button onClick={() => navigate("/impegnative")} className="flex items-center gap-1 text-sm text-[var(--muted)]" data-testid="imp-back-button"><ArrowLeft className="h-4 w-4" /> Impegnative</button>
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="font-heading text-2xl sm:text-3xl font-bold font-mono" data-testid="imp-number">{isNew ? "Nuova impegnativa" : imp.number}</h1>
          {imp && <ImpBadge status={imp.status} />}
          {imp && imp.versions.length > 1 && (
            <select value={ver} onChange={(e) => setVer(Number(e.target.value))} className="h-9 rounded-full border px-3 text-sm bg-white" data-testid="imp-version-select">
              {imp.versions.map((x) => <option key={x.version} value={x.version}>Versione {x.version}{x.signature ? " (firmata)" : ""}</option>)}
            </select>
          )}
        </div>
        {imp && <p className="text-xs text-[var(--muted)]">Creata il {new Date(imp.created_at).toLocaleString("it-IT")} da {imp.created_by}</p>}
        {imp && <Actions imp={imp} ver={ver} onChange={apply} onSave={save} dirty={dirty} />}
      </div>
      {editable && ver === (imp?.current_version ?? ver) ? <ImpForm f={f} setF={setForm} /> : shown && <ImpView v={shown} />}
      {imp && imp.status === "in_attesa_firma" && !cur.signature && ver === imp.current_version && (dirty
        ? <p className="rounded-xl bg-amber-50 border border-amber-200 p-4 text-sm" data-testid="imp-unsaved-warning">Salva le modifiche prima di firmare.</p>
        : <ImpSign imp={imp} conditions={conditions} onSigned={apply} />)}
      {editable && (isNew || dirty) && (
        <div className="fixed bottom-20 lg:bottom-6 inset-x-4 lg:left-auto lg:right-10 z-30 flex justify-center">
          <button onClick={save} disabled={saving} className="btn-primary h-12 px-6 shadow-xl" data-testid="imp-save-button">
            {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : <Save className="h-5 w-5" />} {isNew ? "Crea impegnativa" : "Salva modifiche"}
          </button>
        </div>
      )}
    </div>
  );
}
